import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthTokenMonitorService } from '../../../core/auth/token-monitor.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-session-expiry-dialog',
  template: `
    <div class="session-expiry-dialog">
      <div class="flex flex-col items-center gap-4 p-4">
        <!-- Icono de advertencia -->
        <div class="warning-icon">
          <i class="pi pi-clock" style="font-size: 3rem; color: var(--orange-500);"></i>
        </div>

        <!-- Mensaje principal -->
        <div class="text-center">
          <h3 class="m-0 mb-2 font-bold">Su sesión está por expirar</h3>
          <p class="m-0 text-600">Por seguridad, su sesión se cerrará automáticamente en:</p>
        </div>

        <!-- Contador regresivo -->
        <div class="countdown-timer">
          <span class="time-display">{{ formattedTime }}</span>
        </div>

        <!-- Pregunta -->
        <p class="m-0 text-center text-700">¿Desea continuar trabajando en la plataforma?</p>

        <!-- Botones de acción -->
        <div class="flex gap-3 w-full justify-center mt-2">
          <p-button label="No, cerrar sesión" severity="secondary" [outlined]="true" icon="pi pi-sign-out" (onClick)="onDecline()" [disabled]="isRefreshing" />
          <p-button label="Sí, continuar" severity="primary" icon="pi pi-check" (onClick)="onExtend()" [loading]="isRefreshing" [disabled]="isRefreshing" />
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .session-expiry-dialog {
        min-width: 350px;
      }

      .warning-icon {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: var(--orange-100);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: pulse 2s infinite;
      }

      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.4);
        }
        70% {
          box-shadow: 0 0 0 15px rgba(249, 115, 22, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(249, 115, 22, 0);
        }
      }

      .countdown-timer {
        background: var(--surface-100);
        border-radius: 8px;
        padding: 1rem 2rem;
        border: 2px solid var(--orange-300);
      }

      .time-display {
        font-size: 2.5rem;
        font-weight: 700;
        font-family: 'Courier New', monospace;
        color: var(--orange-600);
        letter-spacing: 0.1em;
      }

      :host ::ng-deep .p-button {
        min-width: 150px;
      }
    `,
  ],
  standalone: false,
})
export class SessionExpiryDialogComponent implements OnInit, OnDestroy {
  formattedTime: string = '00:00';
  isRefreshing: boolean = false;
  private subscription?: Subscription;

  constructor(
    private ref: DynamicDialogRef,
    private tokenMonitor: AuthTokenMonitorService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Actualizar tiempo formateado cada segundo
    this.updateTime();
    this.subscription = new Subscription();

    // Escuchar cambios en el tiempo restante
    const interval = setInterval(() => {
      this.updateTime();
      this.isRefreshing = this.tokenMonitor.isRefreshing();

      // Si el tiempo llegó a 0, cerrar el diálogo
      if (this.tokenMonitor.timeRemaining() <= 0) {
        clearInterval(interval);
        this.ref.close({ action: 'expired' });
      }
    }, 500);

    this.subscription.add(() => clearInterval(interval));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private updateTime(): void {
    this.formattedTime = this.tokenMonitor.formatTimeRemaining();
  }

  async onExtend(): Promise<void> {
    this.isRefreshing = true;
    const success = await this.tokenMonitor.extendSession();
    this.isRefreshing = false;

    if (success) {
      this.ref.close({ action: 'extended' });
    }
    // Si falla, el tokenMonitor manejará la redirección
  }

  onDecline(): void {
    this.tokenMonitor.declineExtension();
    this.ref.close({ action: 'declined' });
  }
}
